import React, { memo, useCallback } from 'react';
import type { Node, NodeProps } from '@xyflow/react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Icon } from '@cotera/client/app/components/ui/icon/icon';
import { ModelScopeFactory } from '@cotera/client/v0/actions/framework';
import { WorkflowTrigger } from './trigger';
import type { WorkflowTriggerViewData } from './trigger';
import { useWorkflowTriggerViewModel } from '../use-workflow-trigger-view-model';
import { WORKFLOW_SELECTED_CLASS } from './node-selection';

export type WorkflowFlowTriggerData = {
  trigger: WorkflowTriggerViewData;
  /** Workflow ↔ trigger binding row; used to archive from the canvas. */
  bindingId?: string;
  /**
   * Whether this trigger will actually fire — the binding and the trigger row
   * both live. Set alongside `bindingId` by `workflowGraphToFlow`, which owns
   * the rule; absent on surfaces that never had a binding to read (previews).
   */
  running?: boolean;
};

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

function webhookNeedsSetup(trigger: WorkflowTriggerViewData): boolean {
  return (
    trigger.kind === PersistedWorkflowTriggerKind.Webhook &&
    (trigger.installStatus !== 'active' || trigger.hasSecret !== true)
  );
}

export const WORKFLOW_FLOW_TRIGGER_TYPE = 'workflowTrigger' as const;

export const WorkflowFlowTrigger = memo(function WorkflowFlowTrigger({
  data,
  selected,
}: NodeProps<Node<WorkflowFlowTriggerData>>) {
  const triggerData = data as WorkflowFlowTriggerData;
  const viewModel = useWorkflowTriggerViewModel(triggerData.trigger);
  const needsSetup = webhookNeedsSetup(triggerData.trigger);
  // Register the trigger model via ModelScopeFactory (deps-compared effect) so
  // an unstable `viewModel` object no longer re-registers every render, which
  // churned add/removeModel → notify() → infinite re-render.
  const createModels = useCallback(
    () => (viewModel !== null ? [viewModel.model] : []),
    [viewModel]
  );

  return (
    <ModelScopeFactory
      createModels={createModels}
      deps={[viewModel?.model.id ?? null]}
    >
      <div className="group relative w-fit">
        <WorkflowTrigger
          trigger={triggerData.trigger}
          className={selected === true ? WORKFLOW_SELECTED_CLASS : undefined}
          bindingId={triggerData.bindingId}
          running={triggerData.running ?? true}
        />
        <div className="absolute -right-2 -top-2 flex items-center gap-1">
          {needsSetup ? (
            <div
              title="Webhook not configured — set up this webhook to run"
              aria-label="Webhook not configured — set up to run"
              className="nodrag flex h-5 w-5 items-center justify-center rounded-full border bg-background text-amber-500 shadow-sm"
            >
              <Icon icon="warning" size="small" />
            </div>
          ) : null}
        </div>
      </div>
    </ModelScopeFactory>
  );
});
